import Icon from '@utilities/icon';
import classNames from 'classnames';
import React, { useEffect, useState, useRef } from 'react';
import isChrome from '@utilities/isChrome';
import useIsMounted from '@hooks/useIsMounted';

interface SpeechToTextProps {
  onChange: (result: string) => void;
  onInterim?: (interim: string) => void;
  className?: string;
}

//TODO: Instead of spread functions create a hook that returns type of device software
function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

const SpeechToTextButton = ({
  onChange,
  className,
}: SpeechToTextProps) => {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [canRender, setCanRender] = useState(false);
  const recognitionRef = useRef<any>(null);

  const isMounted = useIsMounted()

  useEffect(() => {
    if (!isMounted) return;
    setCanRender(isChrome() && !isIOS());

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setIsSupported(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = 'fa-IR';

    recognition.onresult = (event) => {
      let interim = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];

        if (result.isFinal) {
          const newText = result[0].transcript.trim();
          onChange(newText);
        } else {
          interim += result[0].transcript;
        }
      }
    };

    recognition.onerror = () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        setIsListening(false);
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
    };
  }, [isMounted]);

  const startListening = () => {
    if (!recognitionRef.current) return;

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (error) {
        console.error('Speech recognition start failed:', error);
      }
    }
  };

  const stopListening = () => {
    if (!recognitionRef.current) return;
    recognitionRef.current.stop();
    setIsListening(false);
  };

  if (!isSupported || !canRender) {
    return null;
  }

  return (
    <div
      className={classNames(
        className,
        'absolute w-10 h-10 flex justify-center items-center',
      )}
    >
      {isListening && (
        <div className="absolute bottom-full h-fit left-10 top-2 mb-1 px-2 py-1 bg-black text-white text-xs text-nowrap rounded">
          در حال گوش دادن...
          <div className="absolute left-[-6px] top-1/2 -translate-y-1/2 w-0 h-0 border-t-4 border-b-4 border-r-8 border-t-transparent border-b-transparent border-r-black" />
        </div>
      )}
      <button
        type="button"
        onMouseDown={startListening}
        onMouseUp={stopListening}
        onTouchStart={startListening}
        onTouchEnd={stopListening}
        onMouseLeave={stopListening}
        className="w-[28px] h-[28px] flex items-center justify-center text-white rounded-full bg-white shadow-lg z-10 relative"
      >
        <Icon
          name={isListening ? 'MicrophoneFill' : 'Microphone'}
          width="1.2"
          height="1.2"
          fill="#333"
        />
      </button>
    </div>
  );
};

export default SpeechToTextButton;
